import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiX, FiUser, FiHash, FiKey, FiEdit3, FiFileText, FiTag, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiGlobe, FiCopy, FiLock, FiMessageSquare, FiCheckCircle, FiCloud, FiLink2, FiUserX, FiUserCheck, FiPercent, FiChevronUp, FiChevronDown, FiTruck } from 'react-icons/fi';
import {
  listarClientes, criarCliente, atualizarCliente,
  buscarVinculoNibo, buscarCandidatosNibo, vincularClienteNibo,
  criarClienteNibo, atualizarClienteNibo, desvincularClienteNibo, criarContatoClienteNibo,
  atualizarStatusCliente,
} from '../../services/clientes';
import { buscarParametrosNf } from '../../services/nf';
import AliquotaSimplesMensal from '../nf/AliquotaSimplesMensal';
import ModalTomadores from '../nf/emitir/ModalTomadores';
import { avisoSerieDps } from '../../services/nfPerfis';
import PerfisTributacaoNf from './PerfisTributacaoNf';
import SeletorMunicipio from '../../componentes/SeletorMunicipio';
import DadosReceita from './DadosReceita';
import { formatarDocumento } from '../../utils/documento';
import { listarAcessos, criarAcesso, atualizarAcesso, excluirAcesso } from '../../services/acessos';
import { listarRegistros, atualizarRegistro } from '../../services/etapas';
import styles from './Clientes.module.css';
import useEsc from '../../hooks/useEsc';

const LABEL_SITUACAO_LICENCIAMENTO = {
  regular: 'Regular', irregular: 'Irregular', dispensado: 'Dispensado', nao_analisado: 'Não analisado',
};

/* formata CNPJ/CPF pra exibição -- funciona tanto pra dígitos crus (ex.:
   vindo da importação da Nibo) quanto pra valor já mascarado (ex.: digitado
   no formulário de Adicionar), porque sempre limpa antes de remontar */

/* máscara "ao vivo" pra um campo único de CNPJ/CPF -- decide o formato pelo
   tamanho digitado até agora (<=11 dígitos vira CPF, senão CNPJ) */
function mascaraDocumento(v) {
  const d = (v || '').replace(/\D/g, '').slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2');
  }
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

/* ====================================================
   Modais de ação — em branco (conteúdo virá depois)
   ==================================================== */

function DrawerBase({ titulo, icone: Icone, corIcone, cliente, onFechar, children, acoes }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onFechar(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  return (
    <div className={styles.drawerOverlay}>
      <div className={styles.drawer} onClick={e => e.stopPropagation()}>
        <div className={styles.drawerHeader}>
          <div className={styles.drawerHeaderLeft}>
            <span className={styles.drawerIcone} style={{ background: corIcone + '28' }}>
              <Icone size={18} style={{ color: corIcone }} />
            </span>
            <div>
              <p className={styles.drawerSubtitulo}>{cliente.codigo} — {cliente.nome}</p>
              <h2 className={styles.drawerTitulo}>{titulo}</h2>
            </div>
          </div>
          <div className={styles.drawerHeaderRight}>
            {acoes}
            <button className={styles.drawerBtnFechar} onClick={onFechar} type="button">
              <FiX size={18} />
            </button>
          </div>
        </div>
        <div className={styles.drawerBody}>
          {children}
        </div>
      </div>
    </div>
  );
}

const FORM_ACESSO_VAZIO = { descricao: '', usuario: '', senha: '', link: '', observacoes: '' };

function AcessosModal({ cliente, onFechar }) {
  const [acessos, setAcessos]           = useState([]);
  const [carregando, setCarregando]     = useState(true);
  const [erroGeral, setErroGeral]       = useState('');
  const [view, setView]                 = useState('lista'); // 'lista' | 'form'
  const [editando, setEditando]         = useState(null);
  const [form, setForm]                 = useState(FORM_ACESSO_VAZIO);
  const [salvando, setSalvando]         = useState(false);
  const [erroForm, setErroForm]         = useState('');
  const [senhasVisiveis, setSenhasVisiveis] = useState(new Set());
  const [excluindo, setExcluindo]       = useState(null);

  useEffect(() => {
    if (cliente.id === '__mock__') { setCarregando(false); return; }
    listarAcessos(cliente.id)
      .then(setAcessos)
      .catch(e => setErroGeral(e.message))
      .finally(() => setCarregando(false));
  }, [cliente.id]);

  function abrirFormNovo() {
    setEditando(null);
    setForm(FORM_ACESSO_VAZIO);
    setErroForm('');
    setView('form');
  }

  function abrirFormEditar(ac) {
    setEditando(ac);
    setForm({ descricao: ac.descricao, usuario: ac.usuario, senha: ac.senha, link: ac.link, observacoes: ac.observacoes });
    setErroForm('');
    setView('form');
  }

  function fecharForm() {
    setView('lista');
    setEditando(null);
    setForm(FORM_ACESSO_VAZIO);
    setErroForm('');
  }

  async function handleSalvar(e) {
    e.preventDefault();
    if (!form.descricao.trim()) return setErroForm('Descrição é obrigatória');
    setSalvando(true);
    setErroForm('');
    try {
      if (editando) {
        const updated = await atualizarAcesso(editando.id, { ...form });
        setAcessos(prev => prev.map(a => a.id === updated.id ? updated : a));
      } else {
        const novo = await criarAcesso({ cliente_id: cliente.id, ...form });
        setAcessos(prev => [novo, ...prev]);
      }
      fecharForm();
    } catch (err) {
      setErroForm(err.message);
    } finally {
      setSalvando(false);
    }
  }

  async function handleExcluir(id) {
    if (!window.confirm('Excluir este acesso?')) return;
    setExcluindo(id);
    try {
      await excluirAcesso(id);
      setAcessos(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      setErroGeral(err.message);
    } finally {
      setExcluindo(null);
    }
  }

  function toggleSenha(id) {
    setSenhasVisiveis(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function copiar(texto) {
    try { await navigator.clipboard.writeText(texto); } catch {}
  }

  function handleFormChange(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  return (
    <DrawerBase titulo="Acessos" icone={FiKey} corIcone="#4A90D9" cliente={cliente} onFechar={onFechar}>
      {view === 'form' ? (
        <div className={styles.acessoForm}>
          <div className={styles.acessoFormNav}>
            <button type="button" className={styles.btnVoltar} onClick={fecharForm}>
              <FiX size={13} /> Cancelar
            </button>
            <span className={styles.acessoFormTitulo}>{editando ? 'Editar Acesso' : 'Novo Acesso'}</span>
          </div>
          <form onSubmit={handleSalvar} className={styles.acessoFormBody}>
            <div className={styles.campo}>
              <label className={styles.label}>Descrição *</label>
              <input className={styles.input} value={form.descricao} onChange={e => handleFormChange('descricao', e.target.value)} placeholder="Ex: Prefeitura Suzano" autoFocus />
            </div>
            <div className={styles.campo}>
              <label className={styles.label}>Usuário</label>
              <input className={styles.input} value={form.usuario} onChange={e => handleFormChange('usuario', e.target.value)} placeholder="Login ou e-mail" />
            </div>
            <div className={styles.campo}>
              <label className={styles.label}>Senha</label>
              <input className={styles.input} value={form.senha} onChange={e => handleFormChange('senha', e.target.value)} placeholder="Senha do sistema" />
            </div>
            <div className={styles.campo}>
              <label className={styles.label}>Link (URL)</label>
              <input className={styles.input} value={form.link} onChange={e => handleFormChange('link', e.target.value)} placeholder="https://exemplo.gov.br" />
            </div>
            <div className={styles.campo}>
              <label className={styles.label}>Observações</label>
              <textarea className={`${styles.input} ${styles.textarea}`} value={form.observacoes} onChange={e => handleFormChange('observacoes', e.target.value)} placeholder="Anotações adicionais..." rows={3} />
            </div>
            {erroForm && <p className={styles.erro}>{erroForm}</p>}
            <div className={styles.modalFooterForm}>
              <button type="button" className={styles.btnCancelar} onClick={fecharForm} disabled={salvando}>Cancelar</button>
              <button type="submit" className={styles.btnSalvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar Acesso'}</button>
            </div>
          </form>
        </div>
      ) : (
        <>
          <div className={styles.acessosToolbar}>
            <span className={styles.acessosContador}>{acessos.length} acesso{acessos.length !== 1 ? 's' : ''}</span>
            <button type="button" className={styles.btnIncluirAcesso} onClick={abrirFormNovo}>
              <FiPlus size={13} /> Incluir Acesso
            </button>
          </div>

          {erroGeral && <div className={styles.erroFaixa}>{erroGeral}</div>}

          {carregando ? (
            <p className={styles.vazio}>Carregando...</p>
          ) : acessos.length === 0 ? (
            <div className={styles.acessosVazio}>
              <FiKey size={32} className={styles.vazioIcon} />
              <p>Nenhum acesso cadastrado</p>
              <button type="button" className={styles.btnIncluirAcessoEmpty} onClick={abrirFormNovo}>
                <FiPlus size={13} /> Incluir primeiro acesso
              </button>
            </div>
          ) : (
            <div className={styles.acessosList}>
              {acessos.map(ac => {
                const senhaVisivel = senhasVisiveis.has(ac.id);
                const apagando    = excluindo === ac.id;
                return (
                  <div key={ac.id} className={styles.acessoCard}>
                    {/* Cabeçalho: descrição + editar/excluir */}
                    <div className={styles.acessoCardHeader}>
                      <div className={styles.acessoDescricao}>
                        <FiTag size={12} />
                        <span>{ac.descricao}</span>
                      </div>
                      <div className={styles.acessoCardAcoes}>
                        <button type="button" className={styles.btnAcessoEdit} onClick={() => abrirFormEditar(ac)} title="Editar">
                          <FiEdit2 size={12} />
                        </button>
                        <button type="button" className={styles.btnAcessoDelete} onClick={() => handleExcluir(ac.id)} disabled={apagando} title="Excluir">
                          <FiTrash2 size={12} />
                        </button>
                      </div>
                    </div>

                    {/* Linha 1: usuário + senha */}
                    <div className={styles.acessoRow1}>
                      {ac.usuario && (
                        <div className={styles.acessoCampo}>
                          <FiUser size={12} className={styles.campoIcone} />
                          <span className={styles.campoValor}>{ac.usuario}</span>
                          <button type="button" className={styles.btnCopiar} onClick={() => copiar(ac.usuario)} title="Copiar usuário">
                            <FiCopy size={11} />
                          </button>
                        </div>
                      )}
                      {ac.senha && (
                        <div className={styles.acessoCampo}>
                          <FiLock size={12} className={styles.campoIcone} />
                          <span className={styles.campoValor}>{senhaVisivel ? ac.senha : '••••••••'}</span>
                          <button type="button" className={styles.btnCopiar} onClick={() => toggleSenha(ac.id)} title={senhaVisivel ? 'Ocultar senha' : 'Revelar senha'}>
                            {senhaVisivel ? <FiEyeOff size={11} /> : <FiEye size={11} />}
                          </button>
                          <button type="button" className={styles.btnCopiar} onClick={() => copiar(ac.senha)} title="Copiar senha">
                            <FiCopy size={11} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Linha 2: link + observações (discreto) */}
                    {(ac.link || ac.observacoes) && (
                      <div className={styles.acessoRow2}>
                        {ac.link && (
                          <div className={styles.acessoCampoSm}>
                            <FiGlobe size={11} className={styles.campoIconeSm} />
                            <a href={ac.link} target="_blank" rel="noopener noreferrer" className={styles.acessoLink}>{ac.link}</a>
                            <button type="button" className={styles.btnCopiarSm} onClick={() => copiar(ac.link)} title="Copiar link">
                              <FiCopy size={10} />
                            </button>
                          </div>
                        )}
                        {ac.observacoes && (
                          <div className={styles.acessoCampoSm}>
                            <FiMessageSquare size={11} className={styles.campoIconeSm} />
                            <span className={styles.acessoObs}>{ac.observacoes}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </DrawerBase>
  );
}


/* ── prioridade de exibição dos campos do onboard ── */
const ORDEM_CAMPOS = [
  'nire', 'inscrição estadual', 'inscricao estadual', 'ie',
  'inscrição municipal', 'inscricao municipal', 'im', 'iss',
  'conselho de classe', 'crc', 'crea', 'crm', 'crf',
  'alvará', 'alvara', 'licença', 'licenca',
  'sindical', 'sindicato',
  'contato', 'contatos', 'telefone', 'email', 'e-mail',
];

function ordemCampo(nome) {
  const n = (nome || '').toLowerCase();
  const i = ORDEM_CAMPOS.findIndex(o => n.includes(o));
  return i === -1 ? 999 : i;
}

function DadosCadastraisModal({ cliente, onFechar, onAtualizado }) {
  const [registros, setRegistros]   = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]             = useState('');
  const [modoEdicao, setModoEdicao] = useState(false);
  const [editados, setEditados]     = useState({});
  const [dadosEditados, setDadosEditados] = useState(null);
  const [salvando, setSalvando]     = useState(false);
  const [erroSalvar, setErroSalvar] = useState('');
  const [atualizandoStatus, setAtualizandoStatus] = useState(false);

  useEffect(() => {
    if (!cliente.leadId) { setCarregando(false); return; }
    listarRegistros(cliente.leadId)
      .then(data => setRegistros([...data].sort((a, b) => ordemCampo(a.nome_campo) - ordemCampo(b.nome_campo))))
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [cliente.leadId]);

  function onEditChange(id, field, value) {
    setEditados(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  }

  /* entra em edição sempre com os dados atuais do cliente -- inclusive pra
     quem veio importado da Nibo e nunca passou pelo onboard (sem leadId,
     sem "registros"): aqui é onde dá pra completar nome, documento,
     telefone e e-mail direto, sem precisar recriar o cadastro do zero. */
  function iniciarEdicao() {
    setDadosEditados({
      nome:      cliente.nome     || '',
      documento: cliente.cnpj || cliente.cpf || '',
      telefone:  cliente.telefone || '',
      email:     cliente.email    || '',
      nfEssensials: !!cliente.nfEssensials,
      nfCliente:    !!cliente.nfCliente,
      cep: cliente.cep || '', logradouro: cliente.logradouro || '',
      numero: cliente.numero || '', complemento: cliente.complemento || '',
      bairro: cliente.bairro || '', codigoMunicipio: cliente.codigoMunicipio || '',
      nomeFantasia: cliente.nomeFantasia || '', naturezaJuridica: cliente.naturezaJuridica || '',
      porte: cliente.porte || '', situacaoCadastral: cliente.situacaoCadastral || '',
      dataAbertura: (cliente.dataAbertura || '').slice(0, 10),
      capitalSocial: cliente.capitalSocial ?? '',
      cnaesSecundarios: cliente.cnaesSecundarios || [],
      opcaoSimples: cliente.opcaoSimples, opcaoMei: cliente.opcaoMei,
      enquadramento: cliente.enquadramento || '',
      tipoAtividade: cliente.tipoAtividade ?? '',
      cnaePrincipal: cliente.cnaePrincipal || '',
      cnaePrincipalDescricao: cliente.cnaePrincipalDescricao || '',
    });
    setModoEdicao(true);
  }

  async function salvar() {
    setSalvando(true);
    setErroSalvar('');
    try {
      const digitos = dadosEditados.documento.replace(/\D/g, '');
      const atualizado = await atualizarCliente(cliente.id, {
        nome:     dadosEditados.nome.trim(),
        telefone: dadosEditados.telefone.trim(),
        email:    dadosEditados.email.trim(),
        cnpj:     digitos.length > 11 ? digitos : '',
        cpf:      digitos.length > 0 && digitos.length <= 11 ? digitos : '',
        nf_essensials: dadosEditados.nfEssensials,
        nf_cliente:    dadosEditados.nfCliente,
        cep: dadosEditados.cep, logradouro: dadosEditados.logradouro,
        numero: dadosEditados.numero, complemento: dadosEditados.complemento,
        bairro: dadosEditados.bairro, codigo_municipio: dadosEditados.codigoMunicipio,
        nome_fantasia: dadosEditados.nomeFantasia,
        natureza_juridica: dadosEditados.naturezaJuridica,
        porte: dadosEditados.porte, situacao_cadastral: dadosEditados.situacaoCadastral,
        data_abertura: dadosEditados.dataAbertura || null,
        capital_social: dadosEditados.capitalSocial,
        /* enquadramento e CNAEs à mão: CNPJ recém-aberto ainda não está na
           base da Receita, e sem isso o cadastro ficava travado */
        tipo_atividade: dadosEditados.tipoAtividade === '' ? null : Number(dadosEditados.tipoAtividade),
        opcao_simples: dadosEditados.opcaoSimples ?? null,
        opcao_mei:     dadosEditados.opcaoMei ?? null,
        enquadramento: dadosEditados.enquadramento || null,
        cnae_principal: dadosEditados.cnaePrincipal.replace(/\D/g, ''),
        cnae_principal_descricao: dadosEditados.cnaePrincipalDescricao.trim(),
        cnaes_secundarios: dadosEditados.cnaesSecundarios
          .map(c => ({ codigo: String(c.codigo || '').replace(/\D/g, ''), descricao: (c.descricao || '').trim() }))
          .filter(c => c.codigo),
      });
      onAtualizado(atualizado);

      const ids = Object.keys(editados);
      if (ids.length) {
        await Promise.all(ids.map(id => atualizarRegistro(id, editados[id])));
        setRegistros(prev => prev.map(r => editados[r.id] ? { ...r, ...editados[r.id] } : r));
        setEditados({});
      }
      setModoEdicao(false);
    } catch (e) {
      setErroSalvar(e.message);
    } finally {
      setSalvando(false);
    }
  }

  function cancelar() {
    setEditados({});
    setDadosEditados(null);
    setModoEdicao(false);
    setErroSalvar('');
  }

  /* cliente que veio vinculado da Nibo mas não é mais cliente de verdade --
     inativar aqui some da lista principal (some inativos ficam ocultos por
     padrão lá), sem precisar apagar o cadastro. */
  async function alternarStatus() {
    setAtualizandoStatus(true);
    setErroSalvar('');
    try {
      const atualizado = await atualizarStatusCliente(cliente.id, cliente.status === 'inativo' ? 'ativo' : 'inativo');
      onAtualizado(atualizado);
    } catch (e) {
      setErroSalvar(e.message);
    } finally {
      setAtualizandoStatus(false);
    }
  }

  /* valida direto daqui -- não precisa fechar e caçar a linha na lista pra
     confirmar o cadastro logo depois de terminar de editar */
  async function validar() {
    setAtualizandoStatus(true);
    setErroSalvar('');
    try {
      const atualizado = await atualizarStatusCliente(cliente.id, 'ativo');
      onAtualizado(atualizado);
    } catch (e) {
      setErroSalvar(e.message);
    } finally {
      setAtualizandoStatus(false);
    }
  }

  const comRegistro = registros.filter(r => r.possui);
  const semRegistro = registros.filter(r => !r.possui);

  const acoes = modoEdicao ? (
    <>
      <button className={styles.drawerBtnAcao} onClick={salvar} disabled={salvando || !dadosEditados?.nome?.trim()} type="button">
        {salvando ? 'Salvando...' : 'Salvar'}
      </button>
      <button className={`${styles.drawerBtnAcao} ${styles.drawerBtnAcaoGhost}`} onClick={cancelar} type="button">
        Cancelar
      </button>
    </>
  ) : (
    <>
      {cliente.onboardPendente ? (
        <span className={styles.drawerBloqueado} title="Libera quando o onboard for concluído">
          <FiLock size={13} style={{ marginRight: 5 }} /> Bloqueado até concluir o onboard
        </span>
      ) : (
        <button className={styles.drawerBtnAcao} onClick={iniciarEdicao} type="button">
          <FiEdit2 size={13} style={{ marginRight: 5 }} /> Editar
        </button>
      )}
      {cliente.status === 'pendente_validacao' && !cliente.onboardPendente && (
        <button className={styles.drawerBtnAcao} onClick={validar} disabled={atualizandoStatus} type="button">
          <FiCheckCircle size={13} style={{ marginRight: 5 }} /> Validar
        </button>
      )}
      <button
        className={`${styles.drawerBtnAcao} ${styles.drawerBtnAcaoGhost}`}
        onClick={alternarStatus}
        disabled={atualizandoStatus}
        type="button"
      >
        {cliente.status === 'inativo' ? (
          <><FiUserCheck size={13} style={{ marginRight: 5 }} /> Reativar</>
        ) : (
          <><FiUserX size={13} style={{ marginRight: 5 }} /> Inativar</>
        )}
      </button>
    </>
  );

  return (
    <DrawerBase titulo="Dados Cadastrais" icone={FiUser} corIcone="#059669" cliente={cliente} onFechar={onFechar} acoes={acoes}>

      {erroSalvar && <div className={styles.erroFaixa}>{erroSalvar}</div>}

      {/* ── Identificação básica ── */}
      <div className={styles.dadosSecao}>
        <p className={styles.dadosSecaoTitulo}>Identificação</p>
        <div className={styles.dadosGrid}>
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Código do Cliente</span>
            <span className={styles.dadosValor}>{cliente.codigo}</span>
          </div>
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Razão Social / Nome</span>
            {modoEdicao ? (
              <input
                className={styles.dadosInput}
                value={dadosEditados.nome}
                onChange={e => setDadosEditados(d => ({ ...d, nome: e.target.value }))}
              />
            ) : (
              <span className={styles.dadosValor}>{cliente.nome}</span>
            )}
          </div>
          {(modoEdicao || cliente.cnpj || cliente.cpf) && (
            <div className={styles.dadosCampo}>
              <span className={styles.dadosLabel}>CNPJ / CPF</span>
              {modoEdicao ? (
                <input
                  className={styles.dadosInput}
                  value={dadosEditados.documento}
                  onChange={e => setDadosEditados(d => ({ ...d, documento: mascaraDocumento(e.target.value) }))}
                  placeholder="Só números"
                />
              ) : (
                <span className={`${styles.dadosValor} ${styles.dadosValorMono}`}>{formatarDocumento(cliente.cnpj || cliente.cpf)}</span>
              )}
            </div>
          )}
          {(modoEdicao || cliente.telefone) && (
            <div className={styles.dadosCampo}>
              <span className={styles.dadosLabel}>Telefone</span>
              {modoEdicao ? (
                <input
                  className={styles.dadosInput}
                  value={dadosEditados.telefone}
                  onChange={e => setDadosEditados(d => ({ ...d, telefone: e.target.value }))}
                />
              ) : (
                <span className={styles.dadosValor}>{cliente.telefone}</span>
              )}
            </div>
          )}
          {(modoEdicao || cliente.email) && (
            <div className={styles.dadosCampo}>
              <span className={styles.dadosLabel}>E-mail</span>
              {modoEdicao ? (
                <input
                  className={styles.dadosInput}
                  type="email"
                  value={dadosEditados.email}
                  onChange={e => setDadosEditados(d => ({ ...d, email: e.target.value }))}
                />
              ) : (
                <span className={styles.dadosValor}>{cliente.email}</span>
              )}
            </div>
          )}

          {/* Define em qual lista do menu NF o cliente aparece. São
              independentes: dá pra marcar as duas, uma ou nenhuma. */}
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Listas de NF</span>
            {modoEdicao ? (
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', paddingTop: 2 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!dadosEditados.nfEssensials}
                    onChange={e => setDadosEditados(d => ({ ...d, nfEssensials: e.target.checked }))}
                  />
                  NF-Essensials
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!dadosEditados.nfCliente}
                    onChange={e => setDadosEditados(d => ({ ...d, nfCliente: e.target.checked }))}
                  />
                  NF-Clientes
                </label>
              </div>
            ) : (
              <span className={styles.dadosValor}>
                {[cliente.nfEssensials && 'NF-Essensials', cliente.nfCliente && 'NF-Clientes']
                  .filter(Boolean).join(' · ') || '—'}
              </span>
            )}
          </div>
        </div>
      </div>

      <DadosReceita
        cliente={cliente}
        editando={modoEdicao}
        dados={dadosEditados || {}}
        set={(campo, valor) => setDadosEditados(d => ({ ...d, [campo]: valor }))}
        onAtualizado={onAtualizado}
      />

      {/* ── Licenciamento — preenchido automaticamente ao concluir um
           processo do tipo 'licenciamento' (módulo Processos) ── */}
      {(cliente.situacaoLicenciamento || cliente.vencimentoAvcb || cliente.vencimentoLicenca) && (
        <div className={styles.dadosSecao}>
          <p className={styles.dadosSecaoTitulo}>Licenciamento</p>
          <div className={styles.dadosGrid}>
            {cliente.situacaoLicenciamento && (
              <div className={styles.dadosCampo}>
                <span className={styles.dadosLabel}>Situação</span>
                <span className={styles.dadosValor}>{LABEL_SITUACAO_LICENCIAMENTO[cliente.situacaoLicenciamento] || cliente.situacaoLicenciamento}</span>
              </div>
            )}
            {cliente.dataUltimaRevisaoLicenciamento && (
              <div className={styles.dadosCampo}>
                <span className={styles.dadosLabel}>Última revisão</span>
                <span className={styles.dadosValor}>{new Date(cliente.dataUltimaRevisaoLicenciamento).toLocaleDateString('pt-BR')}</span>
              </div>
            )}
            {cliente.vencimentoAvcb && (
              <div className={styles.dadosCampo}>
                <span className={styles.dadosLabel}>Vencimento AVCB</span>
                <span className={styles.dadosValor}>{new Date(cliente.vencimentoAvcb).toLocaleDateString('pt-BR')}</span>
              </div>
            )}
            {cliente.vencimentoLicenca && (
              <div className={styles.dadosCampo}>
                <span className={styles.dadosLabel}>Vencimento da licença</span>
                <span className={styles.dadosValor}>{new Date(cliente.vencimentoLicenca).toLocaleDateString('pt-BR')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Registros do onboard ── */}
      {carregando ? (
        <p className={styles.vazio}>Carregando registros...</p>
      ) : erro ? (
        <div className={styles.erroFaixa}>{erro}</div>
      ) : !cliente.leadId ? (
        <div className={styles.dadosAviso}>Cliente criado manualmente — sem registros de onboard vinculados.</div>
      ) : registros.length === 0 ? (
        <div className={styles.dadosAviso}>Nenhum registro preenchido no onboard ainda.</div>
      ) : (
        <>
          {comRegistro.length > 0 && (
            <div className={styles.dadosSecao}>
              <p className={styles.dadosSecaoTitulo}>Registros Confirmados</p>
              <div className={styles.dadosGrid}>
                {comRegistro.map(r => (
                  <div key={r.id} className={styles.dadosCampo}>
                    <span className={styles.dadosLabel}>{r.nome_campo}</span>
                    {modoEdicao ? (
                      <input
                        className={styles.dadosInput}
                        value={editados[r.id]?.valor_registro ?? r.valor_registro ?? ''}
                        onChange={e => onEditChange(r.id, 'valor_registro', e.target.value)}
                      />
                    ) : (
                      <span className={`${styles.dadosValor} ${styles.dadosValorMono}`}>{r.valor_registro}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {semRegistro.length > 0 && (
            <div className={styles.dadosSecao}>
              <p className={`${styles.dadosSecaoTitulo} ${styles.dadosSecaoTituloNao}`}>Não Possui</p>
              <div className={styles.dadosGridNao}>
                {semRegistro.map(r => (
                  <div key={r.id} className={`${styles.dadosCampo} ${styles.dadosCampoNao}`}>
                    <div className={styles.dadosCampoNaoTopo}>
                      <span className={styles.dadosCampoNomeCampo}>{r.nome_campo}</span>
                      <span className={styles.dadosBadgeNao}>Não possui</span>
                    </div>
                    {modoEdicao ? (
                      <input
                        className={styles.dadosInput}
                        placeholder="Justificativa (opcional)"
                        value={editados[r.id]?.justificativa ?? r.justificativa ?? ''}
                        onChange={e => onEditChange(r.id, 'justificativa', e.target.value)}
                      />
                    ) : (
                      r.justificativa && <span className={styles.dadosJustificativa}>{r.justificativa}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </DrawerBase>
  );
}

/* ====================================================
   Parâmetros de NF — dados fiscais e certificado digital usados quando
   este cliente é o PRESTADOR de uma NFS-e emitida no módulo NF. Nome,
   CNPJ e e-mail continuam vindo de Dados Cadastrais -- aqui só o que é
   específico da emissão fiscal (município, regime, certificado etc.)
   ==================================================== */

const REGIME_TRIBUTARIO_OPCOES = [
  { valor: 1, label: 'Não optante' },
  { valor: 2, label: 'MEI' },
  { valor: 3, label: 'Simples Nacional' },
];

/* regApTribSN da DPS (leiaute nacional): como o ME/EPP do Simples apura os tributos */
const REGIME_APURACAO_SN_OPCOES = [
  { valor: 1, label: 'Regime de apuração dos tributos federais e municipal pelo Simples Nacional' },
  { valor: 2, label: 'Regime de apuração dos tributos federais pelo Simples Nacional e o ISSQN pela NFS-e conforme respectiva legislação municipal do tributo' },
  { valor: 3, label: 'Regime de apuração dos tributos federais e municipal pela NFS-e conforme respectivas legislações federal e municipal de cada tributo' },
];


/* exportado pra tela de emissão abrir e corrigir pendências do cadastro
   sem perder o que já foi preenchido na nota */
/* bloco de configuração recolhível (Parâmetros de NF): só o título até abrir */
function BlocoConfig({ titulo, aberto, onAlternar, children }) {
  return (
    <div className={styles.dadosSecao} style={{ padding: 0, overflow: 'hidden' }}>
      <button type="button" onClick={onAlternar} aria-expanded={aberto}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 14px',
          border: 'none', background: aberto ? '#f4f8fd' : '#fff', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}>
        <span className={styles.dadosSecaoTitulo} style={{ margin: 0 }}>{titulo}</span>
        {aberto ? <FiChevronUp size={16} color="#4A6B8A" /> : <FiChevronDown size={16} color="#4A6B8A" />}
      </button>
      {aberto && <div style={{ padding: '4px 14px 14px' }}>{children}</div>}
    </div>
  );
}

export function NfConfigModal({ cliente, onFechar, onAtualizado, blocoInicial, perfilInicial, onPerfilSalvo }) {
  const [dados, setDados] = useState({
    nfCodigoMunicipioEmissor: cliente.nfCodigoMunicipioEmissor || '',
    nfInscricaoMunicipal:     cliente.nfInscricaoMunicipal     || '',
    nfSerieDps:               cliente.nfSerieDps               || '1',
    nfIssFlexivel:            !!cliente.nfIssFlexivel,
    nfNumeroDpsInicial:       cliente.nfNumeroDpsInicial ?? '',
    nfInformarIbsCbs:         !!cliente.nfInformarIbsCbs,
    nfSituacaoPisCofins:      cliente.nfSituacaoPisCofins ?? '',
    nfReterInssPadrao:        !!cliente.nfReterInssPadrao,
    nfAliquotaInss:           cliente.nfAliquotaInss ?? '',
    nfPercentualTributos:     cliente.nfPercentualTributos ?? '',
    nfRegimeTributario:       cliente.nfRegimeTributario       ?? 3,
    nfRegimeApuracaoSn:       cliente.nfRegimeApuracaoSn        ?? 1,
    nfRegimeEspecial:         cliente.nfRegimeEspecial          ?? 0,
    nfCodigoTributacaoNacional: cliente.nfCodigoTributacaoNacional || '',
    nfCodigoTributacaoMunicipal: cliente.nfCodigoTributacaoMunicipal || '',
    nfDadosPagamento: cliente.nfDadosPagamento || '',
    nfDescricaoServico:         cliente.nfDescricaoServico         || '',
    nfAliquotaIss:              cliente.nfAliquotaIss              ?? '',
    nfCodigoMunicipioPrestacao: cliente.nfCodigoMunicipioPrestacao || '',
    nfValorPadrao:              cliente.nfValorPadrao              ?? '',
    nfPossuiRetencaoPadrao:   !!cliente.nfPossuiRetencaoPadrao,
    nfTipoRetencaoIssPadrao:  cliente.nfTipoRetencaoIssPadrao   ?? 1,
  });
  /* blocos recolhíveis: todos fechados; abre só o que vai mexer */
  const [abertos, setAbertos] = useState(() => new Set(blocoInicial ? [blocoInicial] : []));
  /* vindo de uma pendência da emissão: rola até o bloco que resolve */
  useEffect(() => {
    if (!blocoInicial) return;
    const t = setTimeout(() => document.getElementById(`nfcfg-${blocoInicial}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    return () => clearTimeout(t);
  }, [blocoInicial]);
  const alternar = (k) => setAbertos(x => { const n = new Set(x); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [carregandoParametros, setCarregandoParametros] = useState(true);
  /* certificado: o do menu Certificados (mesmo arquivo e senha das capturas) */
  const [certificado, setCertificado] = useState(null);

  useEffect(() => {
    buscarParametrosNf(cliente.id)
      .then(p => setCertificado(p.certificado || { configurado: !!p.certificado_configurado }))
      .catch(() => {})
      .finally(() => setCarregandoParametros(false));
  }, [cliente.id]);

  function set(campo, valor) {
    setDados(d => ({ ...d, [campo]: valor }));
  }

  const perfilPendente = useRef(null);
  function fechar() {
    if (perfilPendente.current && !window.confirm('Há um perfil de tributação aberto sem salvar. Fechar mesmo assim e perder o que foi preenchido nele?')) return;
    onFechar();
  }

  async function salvar() {
    setSalvando(true); setErro(''); setSucesso('');
    try {
      const atualizado = await atualizarCliente(cliente.id, {
        nf_codigo_municipio_emissor: dados.nfCodigoMunicipioEmissor.trim(),
        nf_inscricao_municipal:      dados.nfInscricaoMunicipal.trim(),
        nf_serie_dps:                dados.nfSerieDps.trim() || '1',
        nf_iss_flexivel:             dados.nfIssFlexivel,
        nf_numero_dps_inicial:       dados.nfNumeroDpsInicial,
        nf_informar_ibs_cbs:         dados.nfInformarIbsCbs,
        nf_situacao_pis_cofins:      dados.nfSituacaoPisCofins,
        nf_reter_inss_padrao:        dados.nfReterInssPadrao,
        nf_aliquota_inss:            dados.nfAliquotaInss,
        nf_percentual_tributos:      dados.nfPercentualTributos,
        nf_regime_tributario:        Number(dados.nfRegimeTributario),
        nf_regime_apuracao_sn:       Number(dados.nfRegimeApuracaoSn),
        nf_regime_especial:          Number(dados.nfRegimeEspecial) || 0,
        nf_codigo_tributacao_nacional: dados.nfCodigoTributacaoNacional.trim(),
        nf_codigo_tributacao_municipal: dados.nfCodigoTributacaoMunicipal || '',
        nf_dados_pagamento: dados.nfDadosPagamento || '',
        nf_descricao_servico:          dados.nfDescricaoServico.trim(),
        nf_aliquota_iss:               dados.nfAliquotaIss === '' ? '' : Number(dados.nfAliquotaIss),
        nf_codigo_municipio_prestacao: dados.nfCodigoMunicipioPrestacao.trim(),
        nf_valor_padrao:               dados.nfValorPadrao === '' ? '' : Number(dados.nfValorPadrao),
        nf_possui_retencao_padrao:   dados.nfPossuiRetencaoPadrao,
        nf_tipo_retencao_iss_padrao: Number(dados.nfTipoRetencaoIssPadrao),
      });
      onAtualizado(atualizado);
      /* perfil aberto: grava junto -- "Salvar parâmetros" é o botão que a
         pessoa enxerga, e o perfil não pode ficar pra trás calado */
      if (perfilPendente.current) {
        const ok = await perfilPendente.current();
        if (!ok) { setErro('Os parâmetros foram salvos, mas o perfil aberto não -- confira o aviso no bloco de perfis.'); return; }
        setSucesso('Parâmetros e perfil salvos.');
        return;
      }
      setSucesso('Parâmetros salvos.');
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <DrawerBase titulo="Parâmetros de NF" icone={FiPercent} corIcone="#EA580C" cliente={cliente} onFechar={fechar}>
      <p className={styles.dadosAviso} style={{ marginBottom: 16 }}>
        Usados quando <strong>{cliente.nome}</strong> é o prestador de uma NFS-e emitida no módulo NF.
        Nome, CNPJ e e-mail continuam vindo de Dados Cadastrais.
      </p>

      {erro && <div className={styles.erroFaixa}>{erro}</div>}
      {sucesso && <div className={styles.dadosAviso} style={{ color: '#059669' }}>{sucesso}</div>}

      <div id="nfcfg-prestador" />
      <BlocoConfig titulo="Prestador" aberto={abertos.has('prestador')} onAlternar={() => alternar('prestador')}>
        <div className={styles.dadosGrid}>
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Município</span>
            <SeletorMunicipio className={styles.dadosInput} valor={dados.nfCodigoMunicipioEmissor}
              onChange={v => set('nfCodigoMunicipioEmissor', v)} />
          </div>
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Inscrição Municipal</span>
            <input className={styles.dadosInput} value={dados.nfInscricaoMunicipal}
              onChange={e => set('nfInscricaoMunicipal', e.target.value)} />
          </div>
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Série da DPS</span>
            <input className={styles.dadosInput} value={dados.nfSerieDps}
              onChange={e => set('nfSerieDps', e.target.value)} />
            {/* a Receita recusa (E0010) série fora da faixa do tipo de emissor;
                nosso emissor usa webservice, cuja faixa é 1 a 49999 */}
            {avisoSerieDps(dados.nfSerieDps) && (
              <span style={{ fontSize: 11.5, color: '#b45309', marginTop: 4, display: 'block' }}>
                {avisoSerieDps(dados.nfSerieDps)}
              </span>
            )}
          </div>
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Número inicial da DPS</span>
            <input className={styles.dadosInput} type="number" min="1"
              value={dados.nfNumeroDpsInicial}
              onChange={e => set('nfNumeroDpsInicial', e.target.value)}
              placeholder="1" />
            <span style={{ fontSize: 11.5, color: '#6B8CAE', marginTop: 4, display: 'block' }}>
              Piso da numeração: a próxima nota sai deste número ou do seguinte ao
              último emitido aqui, o que for maior. Use quando o cliente também
              emite por outro sistema, pra não repetir número — subir este campo
              passa a valer na hora.
            </span>
          </div>
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Regime tributário</span>
            <select className={styles.dadosInput} value={dados.nfRegimeTributario}
              onChange={e => set('nfRegimeTributario', e.target.value)}>
              {REGIME_TRIBUTARIO_OPCOES.map(o => <option key={o.valor} value={o.valor}>{o.label}</option>)}
            </select>
          </div>
          {String(dados.nfRegimeTributario) === '3' && (
            <div className={styles.dadosCampo}>
              <span className={styles.dadosLabel}>Regime de Apuração dos Tributos no Simples Nacional *</span>
              <select className={styles.dadosInput} value={dados.nfRegimeApuracaoSn}
                onChange={e => set('nfRegimeApuracaoSn', e.target.value)}>
                {REGIME_APURACAO_SN_OPCOES.map(o => <option key={o.valor} value={o.valor}>{o.label}</option>)}
              </select>
            </div>
          )}
        </div>
            </BlocoConfig>

      {String(dados.nfRegimeTributario) === '3' && (
        <BlocoConfig titulo="Alíquota do Simples Nacional (mês a mês)" aberto={abertos.has('alquotadosimplesnaci')} onAlternar={() => alternar('alquotadosimplesnaci')}>
          <AliquotaSimplesMensal clienteId={cliente.id} className={styles.dadosInput} />
                </BlocoConfig>
      )}

      <BlocoConfig titulo="Retenções e corpo da nota" aberto={abertos.has('corpo')} onAlternar={() => alternar('corpo')}>
        <div className={styles.dadosGrid}>
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Possui retenção nas notas?</span>
            <select className={styles.dadosInput} value={dados.nfPossuiRetencaoPadrao ? '1' : '0'}
              onChange={e => set('nfPossuiRetencaoPadrao', e.target.value === '1')}>
              <option value="0">Não</option>
              <option value="1">Sim</option>
            </select>
          </div>
        </div>
        <p className={styles.dadosAviso} style={{ marginTop: 6 }}>
          Com <strong>Sim</strong>, o corpo da nota sai calculado: valor base, adicionais, total, retenções (pelos % do perfil),
          valor líquido, dados de pagamento e vencimento (do cadastro do tomador).
        </p>
        <div className={styles.dadosCampo} style={{ marginTop: 10 }}>
          <span className={styles.dadosLabel}>Dados de pagamento (a conta deste cliente, vale para todos os tomadores)</span>
          <textarea className={styles.dadosInput} rows={4} value={dados.nfDadosPagamento}
            placeholder={'Banco Bradesco S/A\nAg: 0137\nC/C: 21016-1\nPIX: 26.146.448/0001-01'}
            onChange={e => set('nfDadosPagamento', e.target.value)} />
        </div>
      </BlocoConfig>

      <BlocoConfig titulo="Retenção de ISS" aberto={abertos.has('retenodeiss')} onAlternar={() => alternar('retenodeiss')}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!dados.nfIssFlexivel}
            onChange={e => set('nfIssFlexivel', e.target.checked)} />
          ISS flexível (decidir nota a nota)
        </label>
        <p className={styles.dadosAviso} style={{ marginTop: 6 }}>
          Pela LC 116/2003 a retenção depende do local onde o serviço é devido. Marcado, a
          emissão pergunta se retém ou não. Desmarcado, já vai como <strong>não retém</strong> sem perguntar.
        </p>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginTop: 14 }}>
          <input type="checkbox" checked={!!dados.nfInformarIbsCbs}
            onChange={e => set('nfInformarIbsCbs', e.target.checked)} />
          Informar IBS/CBS nas notas deste cliente
        </label>
        <p className={styles.dadosAviso} style={{ marginTop: 6 }}>
          Hoje é característica do cliente, não escolha nota a nota. A partir de janeiro passa a
          ser obrigatório para todos e este campo deixa de existir.
        </p>
            </BlocoConfig>

      <BlocoConfig titulo="Tributos federais" aberto={abertos.has('tributosfederais')} onAlternar={() => alternar('tributosfederais')}>
        <p className={styles.dadosAviso} style={{ marginBottom: 10 }}>
          Valem para todas as notas do cliente. As retenções na fonte (IR, INSS, PIS, COFINS,
          CSLL e ISS) ficam em cada <strong>perfil de tributação</strong>, porque mudam conforme o serviço.
        </p>
        <div className={styles.dadosGrid}>
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Situação tributária do PIS/COFINS</span>
            <select className={styles.dadosInput} value={dados.nfSituacaoPisCofins}
              onChange={e => set('nfSituacaoPisCofins', e.target.value)}>
              <option value="">Não informar</option>
              <option value="1">1 - Operação tributável</option>
              <option value="2">2 - Operação não tributável</option>
              <option value="3">3 - Operação isenta</option>
              <option value="4">4 - Operação imune</option>
              <option value="5">5 - Operação com suspensão</option>
            </select>
          </div>
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>% aproximado dos tributos</span>
            <input className={styles.dadosInput} type="number" step="0.01" min="0"
              value={dados.nfPercentualTributos}
              onChange={e => set('nfPercentualTributos', e.target.value)}
              placeholder="deixe vazio pra não informar" />
            <span style={{ fontSize: 11.5, color: '#6B8CAE', marginTop: 4, display: 'block' }}>
              Lei da transparência (Decreto 8.264/2014). Vazio = não informa na nota.
            </span>
          </div>
        </div>
            </BlocoConfig>

      <div id="nfcfg-certificadodigital" />
      <BlocoConfig titulo="Certificado digital" aberto={abertos.has('certificadodigital')} onAlternar={() => alternar('certificadodigital')}>
        <div className={styles.dadosGrid}>
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Status</span>
            <span className={styles.dadosValor}>
              {carregandoParametros ? 'Verificando...' : certificado?.configurado
                ? <span style={{ color: '#059669', display: 'flex', alignItems: 'center', gap: 4 }}><FiCheckCircle size={13} /> Pronto para emitir</span>
                : <span style={{ color: '#b91c1c' }}>{certificado?.erro || 'Sem certificado'}</span>}
            </span>
          </div>
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Certificado usado</span>
            <span className={styles.dadosValor}>
              {carregandoParametros ? '—' : certificado?.origem === 'certificados'
                ? `${certificado.nome || 'A1'}${certificado.vencimento ? ` · vence ${certificado.vencimento.split('-').reverse().join('/')}` : ''}`
                : certificado?.origem === 'emissao' ? 'Arquivo antigo da emissão (cadastre no menu Certificados)' : '—'}
            </span>
          </div>
        </div>
        <p className={styles.dadosSecaoTitulo} style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, marginTop: 8 }}>
          A emissão usa o certificado A1 cadastrado em <strong>Clientes › Certificados</strong> (arquivo e senha de lá). Para trocar ou renovar, atualize por lá.
        </p>
            </BlocoConfig>

      {/* o perfil é obrigatório na emissão: é dele que saem código do serviço,
          valor, retenções, cobrança e o corpo da nota */}
      <div id="nfcfg-perfis" />
      <BlocoConfig titulo="Perfis de tributação (obrigatório para emitir)" aberto={abertos.has('perfis')} onAlternar={() => alternar('perfis')}>
        <p className={styles.dadosAviso} style={{ marginBottom: 10 }}>
          A emissão exige um perfil: é dele que saem o código do serviço, o valor, as retenções, a cobrança e o corpo da nota.
        </p>
        <PerfisTributacaoNf clienteId={cliente.id} semTitulo editarInicial={perfilInicial} onSalvo={onPerfilSalvo} pendente={perfilPendente} />
      </BlocoConfig>

      <div className={styles.modalFooterForm} style={{ position: 'sticky', bottom: 0, background: '#fff', paddingTop: 10, zIndex: 2 }}>
        <button type="button" className={styles.btnCancelar} onClick={fechar} disabled={salvando}>Fechar</button>
        <button type="button" className={styles.btnSalvar} onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : 'Salvar parâmetros'}
        </button>
      </div>
    </DrawerBase>
  );
}

function ContratosModal({ cliente, onFechar }) {
  return (
    <DrawerBase titulo="Contratos" icone={FiFileText} corIcone="#7c3aed" cliente={cliente} onFechar={onFechar}>
      <div className={styles.drawerPlaceholder}>
        <FiFileText size={36} className={styles.placeholderIcone} />
        <p>Módulo de Contratos</p>
        <p className={styles.placeholderSub}>Em desenvolvimento.</p>
      </div>
    </DrawerBase>
  );
}

/* ====================================================
   Vínculo com o Nibo — cria/vincula o cliente no Nibo e
   cadastra o contato que recebe os documentos (acesso ao portal)
   ==================================================== */

const TAX_REGIME_OPCOES = [
  { v: 0, l: 'Não especificado' }, { v: 1, l: 'Lucro Real' }, { v: 2, l: 'Lucro Presumido' },
  { v: 3, l: 'Lucro Arbitrado' },  { v: 4, l: 'Simples Nacional' }, { v: 5, l: 'MEI' }, { v: 6, l: 'Isento/Imune' },
];
const BUSINESS_ACTIVITY_OPCOES = [
  { v: 0, l: 'Não especificado' }, { v: 1, l: 'Comércio' }, { v: 2, l: 'Serviço' },
  { v: 3, l: 'Comércio e Serviço' }, { v: 4, l: 'Indústria' }, { v: 5, l: 'Comércio e Indústria' },
  { v: 6, l: 'Serviço e Indústria' }, { v: 7, l: 'Comércio, Serviço e Indústria' },
];

function formNiboVazio(cliente) {
  return {
    Name: cliente.nome || '',
    Code: cliente.codigo || '',
    DocumentNumber: (cliente.cnpj || cliente.cpf || '').replace(/\D/g, ''),
    TaxRegime: 0,
    BusinessActivity: 0,
    StateRegistration: '',
    CityRegistration: '',
    Cei: '',
    Caepf: '',
    endereco: { street: '', number: '', complement: '', district: '', city: '', state: '', postalCode: '' },
  };
}

function formNiboDoRegistro(nibo) {
  return {
    Name: nibo.name || '',
    Code: nibo.code || '',
    DocumentNumber: nibo.documentNumber || '',
    TaxRegime: nibo.taxRegime ?? 0,
    BusinessActivity: nibo.businessActivity ?? 0,
    StateRegistration: nibo.stateRegistration || '',
    CityRegistration: nibo.cityRegistration || '',
    Cei: nibo.cei || '',
    Caepf: nibo.caepf || '',
    endereco: {
      street:     nibo.address?.street     || '',
      number:     nibo.address?.number     || '',
      complement: nibo.address?.complement || '',
      district:   nibo.address?.district   || '',
      city:       nibo.address?.city       || '',
      state:      nibo.address?.state      || '',
      postalCode: nibo.address?.postalCode || '',
    },
  };
}

function montarPayloadNibo(form) {
  const { endereco, ...resto } = form;
  return {
    ...resto,
    DocumentNumber: (resto.DocumentNumber || '').replace(/\D/g, ''),
    TaxRegime: Number(resto.TaxRegime),
    BusinessActivity: Number(resto.BusinessActivity),
    CustomerAddress: [{
      Street: endereco.street, Number: endereco.number, Complement: endereco.complement,
      District: endereco.district, City: endereco.city, State: endereco.state, PostalCode: endereco.postalCode,
    }],
  };
}

function FormNibo({ form, setForm, onSubmit, salvando, textoBotao }) {
  function set(campo, valor) { setForm(f => ({ ...f, [campo]: valor })); }
  function setEndereco(campo, valor) { setForm(f => ({ ...f, endereco: { ...f.endereco, [campo]: valor } })); }

  return (
    <form onSubmit={onSubmit}>
      <div className={styles.dadosSecao}>
        <p className={styles.dadosSecaoTitulo}>Identificação</p>
        <div className={styles.niboGrid}>
          <div className={styles.campo}>
            <label className={styles.label}>Nome / Razão Social *</label>
            <input className={styles.input} value={form.Name} onChange={e => set('Name', e.target.value)} required />
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>Código *</label>
            <input className={styles.input} value={form.Code} onChange={e => set('Code', e.target.value)} required />
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>CNPJ / CPF *</label>
            <input className={styles.input} value={form.DocumentNumber} onChange={e => set('DocumentNumber', e.target.value)} required />
          </div>
        </div>
      </div>

      <div className={styles.dadosSecao}>
        <p className={styles.dadosSecaoTitulo}>Enquadramento</p>
        <div className={styles.niboGrid}>
          <div className={styles.campo}>
            <label className={styles.label}>Regime tributário</label>
            <select className={styles.input} value={form.TaxRegime} onChange={e => set('TaxRegime', e.target.value)}>
              {TAX_REGIME_OPCOES.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>Atividade econômica</label>
            <select className={styles.input} value={form.BusinessActivity} onChange={e => set('BusinessActivity', e.target.value)}>
              {BUSINESS_ACTIVITY_OPCOES.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>Inscrição estadual</label>
            <input className={styles.input} value={form.StateRegistration} onChange={e => set('StateRegistration', e.target.value)} />
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>Inscrição municipal</label>
            <input className={styles.input} value={form.CityRegistration} onChange={e => set('CityRegistration', e.target.value)} />
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>CEI</label>
            <input className={styles.input} value={form.Cei} onChange={e => set('Cei', e.target.value)} />
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>CAEPF</label>
            <input className={styles.input} value={form.Caepf} onChange={e => set('Caepf', e.target.value)} />
          </div>
        </div>
      </div>

      <div className={styles.dadosSecao}>
        <p className={styles.dadosSecaoTitulo}>Endereço</p>
        <div className={styles.niboGrid}>
          <div className={styles.campo}>
            <label className={styles.label}>Rua</label>
            <input className={styles.input} value={form.endereco.street} onChange={e => setEndereco('street', e.target.value)} />
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>Número</label>
            <input className={styles.input} value={form.endereco.number} onChange={e => setEndereco('number', e.target.value)} />
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>Complemento</label>
            <input className={styles.input} value={form.endereco.complement} onChange={e => setEndereco('complement', e.target.value)} />
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>Bairro</label>
            <input className={styles.input} value={form.endereco.district} onChange={e => setEndereco('district', e.target.value)} />
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>Cidade</label>
            <input className={styles.input} value={form.endereco.city} onChange={e => setEndereco('city', e.target.value)} />
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>UF</label>
            <input className={styles.input} value={form.endereco.state} onChange={e => setEndereco('state', e.target.value)} maxLength={2} />
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>CEP</label>
            <input className={styles.input} value={form.endereco.postalCode} onChange={e => setEndereco('postalCode', e.target.value)} />
          </div>
        </div>
      </div>

      <div className={styles.modalFooterForm}>
        <button type="submit" className={styles.btnSalvar} disabled={salvando}>
          {salvando ? 'Salvando...' : textoBotao}
        </button>
      </div>
    </form>
  );
}

function NiboModal({ cliente, onFechar }) {
  const [carregando, setCarregando] = useState(true);
  const [status, setStatus]         = useState(null); // { vinculado, nibo, contatos, mock }
  const [erro, setErro]             = useState('');
  const [modo, setModo]             = useState('status'); // 'status' | 'buscar' | 'criar' | 'editar'
  const [form, setForm]             = useState(() => formNiboVazio(cliente));
  const [salvando, setSalvando]     = useState(false);
  const [candidatos, setCandidatos] = useState(null);
  const [buscando, setBuscando]     = useState(false);
  const [formContato, setFormContato] = useState({ name: '', email: '', whatsAppNumber: '' });
  const [salvandoContato, setSalvandoContato] = useState(false);

  const carregarStatus = useCallback(() => {
    setCarregando(true); setErro('');
    buscarVinculoNibo(cliente.id)
      .then(r => {
        setStatus(r);
        if (r.vinculado && r.nibo) setForm(formNiboDoRegistro(r.nibo));
      })
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [cliente.id]);

  useEffect(() => { carregarStatus(); }, [carregarStatus]);

  async function handleBuscar() {
    setBuscando(true); setErro('');
    try {
      const r = await buscarCandidatosNibo(cliente.id);
      setCandidatos(r.items || []);
      setModo('buscar');
    } catch (e) {
      setErro(e.message);
    } finally {
      setBuscando(false);
    }
  }

  async function handleVincularExistente(niboId) {
    setSalvando(true); setErro('');
    try {
      await vincularClienteNibo(cliente.id, niboId);
      setModo('status');
      carregarStatus();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function handleCriar(e) {
    e.preventDefault();
    setSalvando(true); setErro('');
    try {
      await criarClienteNibo(cliente.id, montarPayloadNibo(form));
      setModo('status');
      carregarStatus();
    } catch (e2) {
      setErro(e2.message);
    } finally {
      setSalvando(false);
    }
  }

  async function handleAtualizar(e) {
    e.preventDefault();
    setSalvando(true); setErro('');
    try {
      await atualizarClienteNibo(cliente.id, montarPayloadNibo(form));
      setModo('status');
      carregarStatus();
    } catch (e2) {
      setErro(e2.message);
    } finally {
      setSalvando(false);
    }
  }

  async function handleDesvincular() {
    if (!window.confirm('Desvincular este cliente do Nibo? Isso não apaga nada lá — só remove a ligação aqui no nosso sistema.')) return;
    setSalvando(true); setErro('');
    try {
      await desvincularClienteNibo(cliente.id);
      setStatus({ vinculado: false });
      setForm(formNiboVazio(cliente));
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function handleCriarContato(e) {
    e.preventDefault();
    if (!formContato.name.trim() || !formContato.email.trim()) { setErro('Nome e e-mail do contato são obrigatórios'); return; }
    setSalvandoContato(true); setErro('');
    try {
      await criarContatoClienteNibo(cliente.id, formContato);
      setFormContato({ name: '', email: '', whatsAppNumber: '' });
      carregarStatus();
    } catch (e2) {
      setErro(e2.message);
    } finally {
      setSalvandoContato(false);
    }
  }

  return (
    <DrawerBase titulo="Vínculo com o Nibo" icone={FiCloud} corIcone="#0891B2" cliente={cliente} onFechar={onFechar}>
      {erro && <div className={styles.erroFaixa}>{erro}</div>}

      {carregando ? (
        <p className={styles.vazio}>Carregando...</p>
      ) : !status?.vinculado ? (
        modo === 'buscar' ? (
          <>
            <div className={styles.acessosToolbar}>
              <span className={styles.acessosContador}>
                {candidatos?.length ?? 0} cliente{candidatos?.length !== 1 ? 's' : ''} encontrado{candidatos?.length !== 1 ? 's' : ''} no Nibo
              </span>
              <button type="button" className={styles.btnVoltar} onClick={() => setModo('status')}>
                <FiX size={13} /> Cancelar
              </button>
            </div>
            {candidatos?.length === 0 ? (
              <div className={styles.acessosVazio}>
                <FiCloud size={32} className={styles.vazioIcon} />
                <p>Nenhum cliente com esse CNPJ/CPF encontrado no Nibo.</p>
                <button type="button" className={styles.btnIncluirAcessoEmpty} onClick={() => setModo('criar')}>
                  <FiPlus size={13} /> Criar novo cliente no Nibo
                </button>
              </div>
            ) : (
              <div className={styles.acessosList}>
                {candidatos?.map(c => (
                  <div key={c.id} className={styles.acessoCard}>
                    <div className={styles.acessoCardHeader}>
                      <div className={styles.acessoDescricao}><FiUser size={12} /><span>{c.name}</span></div>
                    </div>
                    <div className={styles.acessoRow1}>
                      <div className={styles.acessoCampo}>
                        <FiHash size={12} className={styles.campoIcone} />
                        <span className={styles.campoValor}>{c.documentNumber}</span>
                      </div>
                    </div>
                    <div className={styles.modalFooterForm}>
                      <button type="button" className={styles.btnSalvar} disabled={salvando} onClick={() => handleVincularExistente(c.id)}>
                        <FiLink2 size={13} /> Vincular este
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : modo === 'criar' ? (
          <>
            <div className={styles.acessosToolbar}>
              <span className={styles.acessosContador}>Criar novo cliente no Nibo</span>
              <button type="button" className={styles.btnVoltar} onClick={() => setModo('status')}>
                <FiX size={13} /> Cancelar
              </button>
            </div>
            <FormNibo form={form} setForm={setForm} onSubmit={handleCriar} salvando={salvando} textoBotao="Criar e vincular no Nibo" />
          </>
        ) : (
          <div className={styles.drawerPlaceholder}>
            <FiCloud size={36} className={styles.placeholderIcone} />
            <p>Este cliente ainda não está vinculado ao Nibo.</p>
            <p className={styles.placeholderSub}>
              Pode ser que ele já exista lá (busque pelo CNPJ/CPF) ou seja um cliente que não vai pro Nibo.
            </p>
            <div className={styles.modalFooterForm} style={{ justifyContent: 'center', gap: 10 }}>
              <button type="button" className={styles.btnCancelar} onClick={handleBuscar} disabled={buscando}>
                <FiSearch size={13} style={{ marginRight: 5 }} /> {buscando ? 'Buscando...' : 'Buscar no Nibo'}
              </button>
              <button type="button" className={styles.btnSalvar} onClick={() => { setForm(formNiboVazio(cliente)); setModo('criar'); }}>
                <FiPlus size={13} style={{ marginRight: 5 }} /> Criar novo cliente no Nibo
              </button>
            </div>
          </div>
        )
      ) : modo === 'editar' ? (
        <>
          <div className={styles.acessosToolbar}>
            <span className={styles.acessosContador}>Editando dados no Nibo</span>
            <button type="button" className={styles.btnVoltar} onClick={() => { setForm(formNiboDoRegistro(status.nibo)); setModo('status'); }}>
              <FiX size={13} /> Cancelar
            </button>
          </div>
          <FormNibo form={form} setForm={setForm} onSubmit={handleAtualizar} salvando={salvando} textoBotao="Salvar no Nibo" />
        </>
      ) : (
        <>
          {status.mock && (
            <div className={styles.erroFaixa}>Não foi possível confirmar com o Nibo agora — mostrando o que temos salvo.</div>
          )}

          <div className={styles.dadosSecao}>
            <p className={styles.dadosSecaoTitulo}>Vinculado ao Nibo</p>
            <div className={styles.dadosGrid}>
              <div className={styles.dadosCampo}>
                <span className={styles.dadosLabel}>Nome no Nibo</span>
                <span className={styles.dadosValor}>{status.nibo?.name || '—'}</span>
              </div>
              <div className={styles.dadosCampo}>
                <span className={styles.dadosLabel}>CNPJ/CPF</span>
                <span className={`${styles.dadosValor} ${styles.dadosValorMono}`}>{status.nibo?.documentNumber || '—'}</span>
              </div>
              <div className={styles.dadosCampo}>
                <span className={styles.dadosLabel}>Regime tributário</span>
                <span className={styles.dadosValor}>{TAX_REGIME_OPCOES.find(o => o.v === status.nibo?.taxRegime)?.l || '—'}</span>
              </div>
              <div className={styles.dadosCampo}>
                <span className={styles.dadosLabel}>Atividade</span>
                <span className={styles.dadosValor}>{BUSINESS_ACTIVITY_OPCOES.find(o => o.v === status.nibo?.businessActivity)?.l || '—'}</span>
              </div>
              <div className={styles.dadosCampo}>
                <span className={styles.dadosLabel}>Cidade/UF</span>
                <span className={styles.dadosValor}>{status.nibo?.address?.city || '—'}/{status.nibo?.address?.state || '—'}</span>
              </div>
            </div>
            <div className={styles.modalFooterForm}>
              <button type="button" className={styles.btnCancelar} onClick={handleDesvincular} disabled={salvando}>
                Desvincular
              </button>
              <button type="button" className={styles.btnSalvar} onClick={() => setModo('editar')}>
                <FiEdit2 size={13} style={{ marginRight: 5 }} /> Editar dados
              </button>
            </div>
          </div>

          <div className={styles.dadosSecao}>
            <p className={styles.dadosSecaoTitulo}>Contatos (acesso do cliente ao Nibo)</p>
            {status.contatos?.length > 0 && (
              <div className={styles.acessosList} style={{ marginBottom: 12 }}>
                {status.contatos.map(c => (
                  <div key={c.id} className={styles.acessoCard}>
                    <div className={styles.acessoCardHeader}>
                      <div className={styles.acessoDescricao}><FiUser size={12} /><span>{c.name}</span></div>
                    </div>
                    <div className={styles.acessoRow1}>
                      <div className={styles.acessoCampo}>
                        <span className={styles.campoValor}>{c.email}</span>
                      </div>
                      {c.whatsAppNumber && (
                        <div className={styles.acessoCampo}>
                          <span className={styles.campoValor}>{c.whatsAppNumber}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={handleCriarContato} className={styles.niboGrid}>
              <div className={styles.campo}>
                <label className={styles.label}>Nome</label>
                <input className={styles.input} value={formContato.name} onChange={e => setFormContato(f => ({ ...f, name: e.target.value }))} placeholder="Nome do contato" />
              </div>
              <div className={styles.campo}>
                <label className={styles.label}>E-mail</label>
                <input className={styles.input} type="email" value={formContato.email} onChange={e => setFormContato(f => ({ ...f, email: e.target.value }))} placeholder="email@cliente.com.br" />
              </div>
              <div className={styles.campo}>
                <label className={styles.label}>WhatsApp</label>
                <input className={styles.input} value={formContato.whatsAppNumber} onChange={e => setFormContato(f => ({ ...f, whatsAppNumber: e.target.value }))} placeholder="+5511999999999" />
              </div>
              <div className={styles.campo} style={{ justifyContent: 'flex-end' }}>
                <button type="submit" className={styles.btnSalvar} disabled={salvandoContato}>
                  <FiPlus size={13} style={{ marginRight: 5 }} /> {salvandoContato ? 'Salvando...' : 'Adicionar contato'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </DrawerBase>
  );
}

/* ====================================================
   Modal de Adição manual
   ==================================================== */

function AdicionarModal({ onFechar, onSalvo }) {
  useEsc(onFechar);
  const [form, setForm]         = useState({ codigo: '', nome: '', cnpj: '' });
  const [erro, setErro]         = useState('');
  const [salvando, setSalvando] = useState(false);

  function mascaraCnpj(v) {
    const d = v.replace(/\D/g, '').slice(0, 14);
    return d
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: name === 'cnpj' ? mascaraCnpj(value) : value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.codigo.trim()) return setErro('Código é obrigatório');
    if (!form.nome.trim())   return setErro('Nome é obrigatório');
    setSalvando(true);
    setErro('');
    try {
      const novo = await criarCliente({
        codigo:  form.codigo.trim(),
        nome:    form.nome.trim(),
        empresa: form.nome.trim(),
        cnpj:    form.cnpj.trim(),
      });
      onSalvo(novo);
    } catch (err) {
      setErro(err.message);
      setSalvando(false);
    }
  }

  return (
    <div className={styles.overlay}>
      <div className={`${styles.modal} ${styles.modalSm}`} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <h2 className={styles.modalTitulo}>Adicionar Cliente</h2>
          </div>
          <button className={styles.btnFechar} onClick={onFechar}>
            <FiX size={18} />
          </button>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.campo}>
            <label className={styles.label}>Código *</label>
            <input
              className={styles.input}
              name="codigo"
              value={form.codigo}
              onChange={handleChange}
              placeholder="Ex: 217"
              autoFocus
            />
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>Nome / Razão Social *</label>
            <input
              className={styles.input}
              name="nome"
              value={form.nome}
              onChange={handleChange}
              placeholder="Nome do cliente ou empresa"
            />
          </div>
          <div className={styles.campo}>
            <label className={styles.label}>CNPJ</label>
            <input
              className={styles.input}
              name="cnpj"
              value={form.cnpj}
              onChange={handleChange}
              placeholder="00.000.000/0000-00"
            />
          </div>
          {erro && <p className={styles.erro}>{erro}</p>}
          <div className={styles.modalFooterForm}>
            <button type="button" className={styles.btnCancelar} onClick={onFechar} disabled={salvando}>
              Cancelar
            </button>
            <button type="submit" className={styles.btnSalvar} disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ====================================================
   Botões de ação por linha
   ==================================================== */

const ACOES = [
  { tipo: 'acessos',     icone: FiKey,      titulo: 'Acessos',          cor: '#4A90D9', permKey: 'clientes/clientes/acessos' },
  { tipo: 'dados',       icone: FiEdit3,    titulo: 'Dados Cadastrais', cor: '#059669', permKey: 'clientes/clientes/dados' },
  { tipo: 'contratos',   icone: FiFileText, titulo: 'Contratos',        cor: '#7c3aed', permKey: 'clientes/clientes/contratos' },
  { tipo: 'nibo',        icone: FiCloud,    titulo: 'Vincular Nibo',    cor: '#0891B2', permKey: 'clientes/clientes/nibo' },
  { tipo: 'nf',          icone: FiPercent,  titulo: 'Parâmetros de NF', cor: '#EA580C', permKey: 'clientes/clientes/nf' },
  { tipo: 'fornecedores', icone: FiTruck,   titulo: 'Fornecedores',     cor: '#0F766E', permKey: 'clientes/clientes/fornecedores' },
];


const CARD_KEYS = ACOES.map(a => a.permKey);

function getClientePerm() {
  try { return JSON.parse(localStorage.getItem('erp_perm')); } catch { return null; }
}

function podeCard(perm, key) {
  if (!perm || perm.admin) return true;
  // backward compat: se nenhuma chave de card foi configurada, mostra todos
  if (!CARD_KEYS.some(k => (perm.menus || []).includes(k))) return true;
  return (perm.menus || []).includes(key);
}

function AcoesBotoes({ cliente, onAbrir }) {
  const perm = getClientePerm();
  return (
    <div className={styles.acoesBotoes}>
      {ACOES.filter(a => podeCard(perm, a.permKey)).map(a => (
        <button
          key={a.tipo}
          className={styles.acaoBtn}
          style={{ '--cor-acao': a.cor }}
          onClick={() => onAbrir(a.tipo, cliente)}
          title={a.titulo}
          type="button"
        >
          <a.icone size={14} />
        </button>
      ))}
    </div>
  );
}

/* ====================================================
   Página principal
   ==================================================== */

function Clientes() {
  const navigate = useNavigate();
  const [clientes, setClientes]         = useState([]);
  const [carregando, setCarregando]     = useState(true);
  const [erro, setErro]                 = useState('');
  const [busca, setBusca]               = useState('');
  const [mostrarAdicionar, setMostrarAdicionar] = useState(false);
  const [validandoId, setValidandoId]   = useState(null);
  const [mostrarInativos, setMostrarInativos] = useState(false);

  /* { tipo: 'acessos'|'dados'|'contratos'|'nibo'|'nf', cliente } */
  const [modalAtivo, setModalAtivo] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      /* única tela que precisa dos inativos: é aqui que se consulta e
         reativa (botão "mostrar inativos" logo abaixo) */
      const reais = await listarClientes({ incluirInativos: true });
      setClientes(reais);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  function handleSalvo(novo) {
    setClientes(prev =>
      [...prev, novo].sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true }))
    );
    setMostrarAdicionar(false);
    /* o cliente já foi salvo aqui; o envio pro Docs acontece em seguida e
       nunca derruba o cadastro -- quando não dá certo, o backend devolve
       um aviso pra pessoa saber que o vínculo ficou pendente */
    setErro(novo?.docs?.aviso || '');
  }

  function abrirModal(tipo, cliente) {
    setModalAtivo({ tipo, cliente });
  }

  function aoAtualizarCliente(atualizado) {
    setClientes(prev => prev.map(c => c.id === atualizado.id ? atualizado : c));
    setModalAtivo(prev => prev?.cliente.id === atualizado.id ? { ...prev, cliente: atualizado } : prev);
  }

  async function handleValidar(cliente) {
    setValidandoId(cliente.id);
    try {
      const atualizado = await atualizarStatusCliente(cliente.id, 'ativo');
      aoAtualizarCliente(atualizado);
    } catch (e) {
      setErro(e.message);
    } finally {
      setValidandoId(null);
    }
  }

  const qtdInativos = clientes.filter(c => c.status === 'inativo').length;

  const filtrados = clientes
    .filter(c => mostrarInativos || c.status !== 'inativo')
    .filter(c => {
      const q = busca.trim().toLowerCase();
      if (!q) return true;
      /* Busca só com números: até 4 dígitos é CÓDIGO, não documento.
         O CNPJ fica guardado só com dígitos, então "110" achava qualquer
         CNPJ terminado em ...000110 junto com o cliente 110. Com 5 dígitos
         ou mais já dá pra ser trecho de CNPJ/CPF, aí procura no documento
         também (com ou sem máscara). */
      if (/^[\d.\-/\s]+$/.test(q)) {
        const digitos = q.replace(/\D/g, '');
        const codigo = (c.codigo || '').toLowerCase();
        /* "1" acha o 001 também; quem digita "001" já deu o código
           completo, então aí compara do jeito que foi digitado */
        const porCodigo = codigo.startsWith(digitos)
          || (!digitos.startsWith('0') && codigo.replace(/^0+/, '').startsWith(digitos));
        if (digitos.length <= 4) return porCodigo;
        return porCodigo || (c.cnpj || '').replace(/\D/g, '').includes(digitos);
      }
      return (
        (c.codigo || '').toLowerCase().includes(q) ||
        (c.nome || '').toLowerCase().includes(q)
      );
    });

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.titulo}>Clientes</h1>
        <div className={styles.pageHeaderAcoes}>
          {/* cliente novo nasce no Leads (fluxo, processos, código) e vai pro
              Docs quando o gerente libera no Onboard -- não se cria mais aqui */}
          <button className={styles.btnAdicionar} onClick={() => navigate('/app/clientes/leads?novo=1')}
            title="O cadastro de cliente novo é feito pelo Leads">
            <FiPlus size={15} /> Novo cliente
          </button>
        </div>
      </div>

      <div className={styles.buscaRow}>
        <div className={styles.buscaWrapper}>
          <FiSearch className={styles.buscaIcon} size={15} />
          <input
            className={styles.buscaInput}
            placeholder="Buscar por código, nome ou CNPJ..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
          {busca && (
            <button className={styles.buscaLimpar} onClick={() => setBusca('')}>
              <FiX size={14} />
            </button>
          )}
        </div>
        {qtdInativos > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4A6B8A', cursor: 'pointer', marginLeft: 14 }}>
            <input type="checkbox" checked={mostrarInativos} onChange={e => setMostrarInativos(e.target.checked)} />
            Mostrar inativos ({qtdInativos})
          </label>
        )}
      </div>

      {erro && <div className={styles.erroFaixa}>{erro}</div>}

      {carregando ? (
        <p className={styles.vazio}>Carregando...</p>
      ) : (
        <div className={styles.tabela}>
          <div className={styles.tabelaHeader}>
            <div className={styles.colCodigo}>Código</div>
            <div className={styles.colNome}>Nome / Documento</div>
            <div className={styles.colStatus}>Status</div>
            <div className={styles.colAcoes}>Ações</div>
          </div>

          {filtrados.length === 0 ? (
            <div className={styles.tabelaVazia}>
              {busca ? (
                <p>Nenhum cliente encontrado para esta busca.</p>
              ) : (
                <>
                  <FiUser size={32} className={styles.vazioIcon} />
                  <p>Nenhum cliente cadastrado.</p>
                  <p className={styles.vazioSub}>
                    Clientes gerados no Onboard ou adicionados manualmente aparecem aqui.
                  </p>
                </>
              )}
            </div>
          ) : (
            filtrados.map(c => (
              <div key={c.id} className={`${styles.tabelaRow} ${c.status === 'inativo' ? styles.rowInativo : ''}`}>
                <div className={styles.colCodigo}>
                  <span className={styles.codigoBadge}>{c.codigo}</span>
                </div>
                <div className={styles.colNome}>
                  <span className={styles.clienteNome}>{c.nome}</span>
                  {(c.cnpj || c.cpf) && (
                    <span className={styles.clienteDoc}>
                      {formatarDocumento(c.cnpj || c.cpf)}
                    </span>
                  )}
                </div>
                <div className={styles.colStatus}>
                  <span className={`${styles.statusBadge} ${
                    c.status === 'pendente_validacao' ? styles.statusPendente
                      : c.status === 'inativo' ? styles.statusInativo
                      : styles.statusAtivo
                  }`}>
                    {c.status === 'pendente_validacao' ? 'Pendente de validação' : c.status === 'inativo' ? 'Inativo' : 'Ativo'}
                  </span>
                  {c.onboardPendente && (
                    <span className={`${styles.statusBadge} ${styles.statusPendente}`} title="Criado na aprovação do lead. Libera quando o onboard for concluído.">
                      <FiLock size={11} style={{ marginRight: 4 }} />Onboard pendente
                    </span>
                  )}
                  {c.status === 'pendente_validacao' && (
                    <button
                      type="button"
                      className={styles.btnValidar}
                      onClick={() => handleValidar(c)}
                      disabled={validandoId === c.id}
                    >
                      <FiCheckCircle size={12} /> {validandoId === c.id ? 'Validando...' : 'Validar'}
                    </button>
                  )}
                </div>
                <div className={styles.colAcoes}>
                  <AcoesBotoes cliente={c} onAbrir={abrirModal} />
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modal Adicionar */}
      {mostrarAdicionar && (
        <AdicionarModal
          onFechar={() => setMostrarAdicionar(false)}
          onSalvo={handleSalvo}
        />
      )}

      {/* Modais de ação */}
      {modalAtivo?.tipo === 'acessos' && (
        <AcessosModal cliente={modalAtivo.cliente} onFechar={() => setModalAtivo(null)} />
      )}
      {modalAtivo?.tipo === 'dados' && (
        <DadosCadastraisModal cliente={modalAtivo.cliente} onFechar={() => setModalAtivo(null)} onAtualizado={aoAtualizarCliente} />
      )}
      {modalAtivo?.tipo === 'contratos' && (
        <ContratosModal cliente={modalAtivo.cliente} onFechar={() => setModalAtivo(null)} />
      )}
      {modalAtivo?.tipo === 'nibo' && (
        <NiboModal cliente={modalAtivo.cliente} onFechar={() => setModalAtivo(null)} />
      )}
      {modalAtivo?.tipo === 'fornecedores' && (
        <ModalTomadores clienteId={modalAtivo.cliente.id} modoCadastro
          titulo={`Fornecedores de ${modalAtivo.cliente.nome}`} onFechar={() => setModalAtivo(null)} />
      )}
      {modalAtivo?.tipo === 'nf' && (
        <NfConfigModal cliente={modalAtivo.cliente} onFechar={() => setModalAtivo(null)} onAtualizado={aoAtualizarCliente} />
      )}
    </div>
  );
}

export default Clientes;
